import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';
import type { Sport } from '@/types';
import { calculateRemainingSlots } from '@/lib/match-utils';

// Re-export for backwards compatibility with existing imports.
export { calculateRemainingSlots };

export type NearbyMatch = Tables<'matches'> & {
  participants?: (Tables<'match_participants'> & {
    profile: Tables<'profiles'>;
  })[];
  created_by_profile?: Tables<'profiles'> | null;
  distance?: number; // km
  hasCoordinates?: boolean;
};

interface NearbyMatchesOptions {
  latitude?: number | null;
  longitude?: number | null;
  radiusKm?: number;
  sport?: Sport | 'all';
  maxHours?: number; // Only matches starting within this many hours
  skillLevel?: string;
  enabled?: boolean;
}

import { calculateDistance } from '@/lib/geo-utils';

// Get today's date in YYYY-MM-DD format for database filtering
function getTodayDateString(): string {
  const now = new Date();
  return now.toISOString().split('T')[0];
}

// Parse match datetime with proper timezone handling
function parseMatchDateTime(date: string, time: string): Date {
  // Create date in local timezone
  const [year, month, day] = date.split('-').map(Number);
  const [hours, minutes, seconds = 0] = time.split(':').map(Number);
  return new Date(year, month - 1, day, hours, minutes, seconds);
}



export function useNearbyMatches({
  latitude,
  longitude,
  radiusKm = 5,
  sport = 'all',
  maxHours = 24,
  skillLevel,
  enabled = true,
}: NearbyMatchesOptions = {}) {
  return useQuery({
    queryKey: ['nearby-matches', latitude, longitude, radiusKm, sport, maxHours, skillLevel],
    queryFn: async () => {
      const today = getTodayDateString();
      
      // Get upcoming matches that are recruiting players
      // Filter at database level: only matches from today onwards
      let query = supabase
        .from('matches')
        .select(`
          *,
          participants:match_participants(
            id, match_id, profile_id, team, is_no_show,
            profile:profiles_public(id, name, avatar, rank, rank_points, skill_level, player_id)
          ),
          created_by_profile:profiles_public!matches_created_by_fkey(id, name, avatar, rank, rank_points, skill_level, location, player_id)
        `)
        .eq('status', 'upcoming')
        .eq('recruiting_players', true)
        .gte('date', today)
        .order('date', { ascending: true })
        .order('time', { ascending: true })
        .limit(50);

      if (sport && sport !== 'all') {
        query = query.eq('sport', sport);
      }

      const { data, error } = await query;
      if (error) throw error;

      let matches = data as NearbyMatch[];
      const now = new Date();

      // Filter by time (only matches within maxHours) with proper timezone handling
      matches = matches.filter((match) => {
        const matchDateTime = parseMatchDateTime(match.date, match.time);
        const hoursUntil = (matchDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);
        return hoursUntil > 0 && hoursUntil <= maxHours;
      });

      // Filter by skill level if specified
      if (skillLevel && skillLevel !== 'all') {
        matches = matches.filter(
          (m) => m.created_by_profile?.skill_level === skillLevel
        );
      }

      // Filter matches with remaining slots (using corrected calculation)
      matches = matches.filter((match) => {
        const creatorRow = (match.participants || []).find(
          (p: any) => (p.profile_id || p.profile?.id) === match.created_by,
        );
        const creatorTeam = (creatorRow as any)?.team ?? 1;
        const remainingSlots = calculateRemainingSlots(
          (match as any).match_format === 'free_play'
            ? (match.recruiting_slots || 0)
            : Math.min(match.recruiting_slots || 0, 1),
          match.participants,
          match.created_by,
          (match as any).match_type,
          creatorTeam,
        );
        return remainingSlots > 0;
      });

      // Mark matches with/without coordinates and calculate distance
      matches = matches.map((match) => {
        const hasCoordinates = match.latitude != null && match.longitude != null;
        return { ...match, hasCoordinates };
      });

      // Calculate distance and filter if location provided
      if (latitude != null && longitude != null) {
        matches = matches
          .map((match) => {
            if (match.latitude && match.longitude) {
              const distance = calculateDistance(latitude, longitude, match.latitude, match.longitude);
              return { ...match, distance };
            }
            return { ...match, distance: undefined };
          })
          .filter((match) => {
            // Include matches within radius OR matches without coordinates
            if (match.distance === undefined) return true;
            return match.distance <= radiusKm;
          })
          .sort((a, b) => {
            // Sort by distance first (matches with distance come first)
            if (a.distance !== undefined && b.distance !== undefined) {
              return a.distance - b.distance;
            }
            if (a.distance !== undefined) return -1;
            if (b.distance !== undefined) return 1;
            // Then by time
            const aTime = parseMatchDateTime(a.date, a.time).getTime();
            const bTime = parseMatchDateTime(b.date, b.time).getTime();
            return aTime - bTime;
          });
      } else {
        // No location - sort by time only
        matches = matches.sort((a, b) => {
          const aTime = parseMatchDateTime(a.date, a.time).getTime();
          const bTime = parseMatchDateTime(b.date, b.time).getTime();
          return aTime - bTime;
        });
      }

      return matches;
    },
    enabled: enabled,
  });
}

// Hook to get urgently needed matches (starting within 2 hours and need players)
export function useUrgentMatches({
  latitude,
  longitude,
  sport = 'all',
  enabled = true,
}: {
  latitude?: number | null;
  longitude?: number | null;
  sport?: Sport | 'all';
  enabled?: boolean;
} = {}) {
  return useQuery({
    queryKey: ['urgent-matches', latitude, longitude, sport],
    queryFn: async () => {
      const today = getTodayDateString();
      
      let query = supabase
        .from('matches')
        .select(`
          *,
          participants:match_participants(
            id, match_id, profile_id, team, is_no_show,
            profile:profiles_public(id, name, avatar, rank, rank_points, skill_level, player_id)
          ),
          created_by_profile:profiles_public!matches_created_by_fkey(id, name, avatar, rank, rank_points, skill_level, location, player_id)
        `)
        .eq('status', 'upcoming')
        .eq('recruiting_players', true)
        .gte('date', today)
        .order('date', { ascending: true })
        .order('time', { ascending: true })
        .limit(20);

      if (sport && sport !== 'all') {
        query = query.eq('sport', sport);
      }

      const { data, error } = await query;
      if (error) throw error;

      let matches = data as NearbyMatch[];
      const now = new Date();

      // Filter to matches starting within 2 hours with proper timezone handling
      matches = matches.filter((match) => {
        const matchDateTime = parseMatchDateTime(match.date, match.time);
        const hoursUntil = (matchDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);
        return hoursUntil > 0 && hoursUntil <= 2;
      });

      // Calculate remaining slots using corrected calculation
      matches = matches.filter((match) => {
        const creatorRow = (match.participants || []).find(
          (p: any) => (p.profile_id || p.profile?.id) === match.created_by,
        );
        const creatorTeam = (creatorRow as any)?.team ?? 1;
        const remainingSlots = calculateRemainingSlots(
          (match as any).match_format === 'free_play'
            ? (match.recruiting_slots || 0)
            : Math.min(match.recruiting_slots || 0, 1),
          match.participants,
          match.created_by,
          (match as any).match_type,
          creatorTeam,
        );
        return remainingSlots > 0;
      });

      // Mark coordinates and calculate distance if location provided
      matches = matches.map((match) => {
        const hasCoordinates = match.latitude != null && match.longitude != null;
        
        if (latitude != null && longitude != null && match.latitude && match.longitude) {
          const distance = calculateDistance(latitude, longitude, match.latitude, match.longitude);
          return { ...match, distance, hasCoordinates };
        }
        return { ...match, distance: undefined, hasCoordinates };
      });

      return matches;
    },
    enabled: enabled,
  });
}
